/**
 * Pure functions that turn the two menu endpoints into the sidebar's `NavTree`.
 *
 * No Angular dependencies on purpose — every branch here is a data-shape decision that is
 * cheaper to unit-test than to debug through a rendered accordion.
 */

import { MenuItemApiDto, ModuleSelectorApiDto } from '../models/menu.model';
import { displayTerm } from '../utils/display-terms';
import {
  ActiveNav,
  EMPTY_ACTIVE,
  NavKind,
  NavModule,
  NavNode,
  NavPathIndexEntry,
  NavPerms,
  NavTree,
} from '../models/nav.model';

/** Labels longer than this truncate at the 280px panel width, so they get a `title`. */
const TITLE_THRESHOLD = 28;

const NO_PERMS: NavPerms = {
  view: false, add: false, edit: false,
  remove: false, exportData: false, importData: false, any: false,
};

// ── Path normalisation ─────────────────────────────────────────────────────────

/**
 * Returns a usable route, or null when the node must not be navigable.
 *
 * Rejects the empty string explicitly: the old page-drawer bound `[routerLink]=""`, which
 * Angular resolves to the CURRENT url — a silent self-navigation that looked like a dead click.
 * Also rejects unfilled route params (`/app/booking/detail/:id`); the server already excludes
 * those via ShowInMenu=false, so this is belt-and-braces.
 */
export function normPath(raw: string | null | undefined): string | null {
  const p = (raw ?? '').trim();
  if (!p || p === '#' || p === '/') return null;
  if (p.includes('/:')) return null;
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
}

/** Lowercased, param/query-free segments. Used only for matching, never for navigation. */
function segsOf(path: string): string[] {
  return path.toLowerCase().split('/').filter(Boolean);
}

// ── DTO -> NavNode ─────────────────────────────────────────────────────────────

function permsOf(d: MenuItemApiDto): NavPerms {
  const view = !!d.isView;
  const add = !!d.isAdd;
  const edit = !!d.isEdit;
  const remove = !!d.isDelete;
  const exportData = !!d.isExport;
  const importData = !!d.isImport;
  return {
    view, add, edit, remove, exportData, importData,
    any: view || add || edit || remove || exportData || importData,
  };
}

function iconFor(raw: string | undefined, kind: NavKind): string {
  if (raw && raw.trim()) return raw.trim();
  return kind === 'module' ? 'bi-grid' : kind === 'section' ? 'bi-folder' : 'bi-circle';
}

function toNode(d: MenuItemApiDto, isRoot: boolean): NavNode {
  const children = (d.children ?? []).map(c => toNode(c, false));
  // Kind from SHAPE, never from d.featureType — see nav.model.ts NavKind.
  const kind: NavKind = isRoot ? 'module' : children.length > 0 ? 'section' : 'page';
  const label = displayTerm((d.aliasName?.trim() || d.featureName || '').trim());

  return {
    id: d.id,
    parentId: d.parentId ?? null,
    kind,
    label,
    icon: iconFor(d.iconName, kind),
    path: normPath(d.path),
    order: d.displayOrder ?? 0,
    needsTitle: label.length > TITLE_THRESHOLD,
    perms: permsOf(d),
    children,
  };
}

// ── Permission filter ──────────────────────────────────────────────────────────

/**
 * Drops nodes the user cannot use. Semantics preserved verbatim from the outgoing
 * `MenuService.filterByPermissions`: a leaf survives if it has ANY permission; a parent
 * survives if any child survived OR it has a permission of its own.
 */
function filterByPerms(nodes: NavNode[]): NavNode[] {
  const out: NavNode[] = [];
  for (const n of nodes) {
    const kids = n.children.length ? filterByPerms(n.children) : [];
    if (n.children.length) {
      if (kids.length > 0 || n.perms.any) out.push({ ...n, children: kids });
    } else if (n.perms.any) {
      out.push(n);
    }
  }
  return out;
}

// ── Default landing page ───────────────────────────────────────────────────────

/**
 * First reachable leaf, depth-first. Section headers are skipped — navigating to one lands on
 * a route that renders nothing. Mirrors the old `getAllAccessiblePages` rule.
 */
function firstAccessibleLeaf(nodes: NavNode[]): string | null {
  for (const n of nodes) {
    if (n.children.length) {
      const deep = firstAccessibleLeaf(n.children);
      if (deep) return deep;
    } else if (n.path && (n.perms.view || n.perms.add || n.perms.edit)) {
      return n.path;
    }
  }
  return null;
}

// ── Orphan adoption ────────────────────────────────────────────────────────────

/**
 * Re-parents pages the server promoted to roots.
 *
 * The server treats a node as a root when its parent is absent from the response
 * (`MenuService.GetMenuForUserAsync`). So when a group is granted a page but NOT its section
 * header, the header is filtered out and the page surfaces as a top-level entry. Rendered
 * naively it becomes a fake module in the rail.
 *
 * Adoption is by longest segment-boundary path prefix. An orphan we cannot place is dropped
 * rather than promoted: a rail icon we cannot label correctly is worse than a missing page,
 * and the dev warning names it precisely so the data can be fixed.
 */
function adoptOrphans(
  roots: NavNode[],
  moduleIds: ReadonlySet<number>,
): { kept: NavNode[]; adopted: Map<number, NavNode[]>; dropped: NavNode[] } {
  const kept: NavNode[] = [];
  const orphans: NavNode[] = [];
  for (const r of roots) (moduleIds.has(r.id) ? kept : orphans).push(r);

  const adopted = new Map<number, NavNode[]>();
  const dropped: NavNode[] = [];

  const moduleSegs = kept
    .filter(m => m.path)
    .map(m => ({ id: m.id, segs: segsOf(m.path!) }))
    .sort((a, b) => b.segs.length - a.segs.length);

  for (const o of orphans) {
    const oSegs = o.path ? segsOf(o.path) : [];
    const host = oSegs.length
      ? moduleSegs.find(m => m.segs.every((s, i) => s === oSegs[i]))
      : undefined;

    if (host) {
      const list = adopted.get(host.id) ?? [];
      list.push(o);
      adopted.set(host.id, list);
    } else {
      dropped.push(o);
    }
  }
  return { kept, adopted, dropped };
}

// ── Path index ─────────────────────────────────────────────────────────────────

/** Flattens every navigable node, deepest-first, for longest-prefix matching. */
export function buildPathIndex(modules: NavModule[]): NavPathIndexEntry[] {
  const out: NavPathIndexEntry[] = [];

  const walk = (n: NavNode, moduleId: number, sectionId: number | null): void => {
    if (n.path) {
      out.push({
        segs: segsOf(n.path), nodeId: n.id, moduleId,
        sectionId, kind: n.kind, order: n.order,
      });
    }
    const nextSection = n.kind === 'section' ? n.id : sectionId;
    for (const c of n.children) walk(c, moduleId, nextSection);
  };

  for (const m of modules) walk(m, m.id, null);

  // Deepest first. Ties: page > section > module, then lower order, then lower id — this is
  // what makes /app/facility resolve to the Branches PAGE rather than stopping at the module.
  const kindRank: Record<NavKind, number> = { page: 0, section: 1, module: 2 };
  return out.sort(
    (a, b) =>
      b.segs.length - a.segs.length ||
      kindRank[a.kind] - kindRank[b.kind] ||
      a.order - b.order ||
      a.nodeId - b.nodeId,
  );
}

// ── Active-node resolution ─────────────────────────────────────────────────────

/**
 * Longest segment-boundary prefix match of the current URL against the index.
 *
 * Segment-boundary, not `startsWith`: `/app/booking` must not match `/app/bookings-archive`.
 * Returns all-null when nothing matches (e.g. `/app/security/profile`, which has no menu row)
 * — showing no highlight is honest; showing the wrong one is not.
 */
export function resolveActive(url: string, index: readonly NavPathIndexEntry[]): ActiveNav {
  const clean = url.split('?')[0].split('#')[0];
  let urlSegs: string[];
  try {
    urlSegs = segsOf(decodeURIComponent(clean));
  } catch {
    urlSegs = segsOf(clean);
  }
  if (!urlSegs.length) return EMPTY_ACTIVE;

  const hit = index.find(e => e.segs.length <= urlSegs.length && e.segs.every((s, i) => s === urlSegs[i]));
  if (!hit) return EMPTY_ACTIVE;

  return {
    moduleId: hit.moduleId,
    sectionId: hit.sectionId,
    pageId: hit.kind === 'module' ? null : hit.nodeId,
  };
}

// ── Entry point ────────────────────────────────────────────────────────────────

export interface BuildNavOptions {
  /** Emits console warnings for dropped orphans. Pass `!environment.production`. */
  warn?: boolean;
}

export function buildNavTree(
  moduleDtos: readonly ModuleSelectorApiDto[],
  treeDtos: readonly MenuItemApiDto[],
  menuPaths: readonly string[] = [],
  opts: BuildNavOptions = {},
): NavTree {
  const roots = (treeDtos ?? []).map(d => toNode(d, true));
  const moduleIds = new Set((moduleDtos ?? []).map(m => m.featureId));

  const { kept, adopted, dropped } = adoptOrphans(roots, moduleIds);
  if (opts.warn && dropped.length) {
    // eslint-disable-next-line no-console
    console.warn(
      '[nav] Dropped %d menu node(s) with no owning module. Grant IsView on the module root, ' +
      'or fix the seeded Path. Affected: %s',
      dropped.length,
      dropped.map(d => `${d.label} (id=${d.id}, path=${d.path ?? 'none'})`).join(', '),
    );
  }

  const rootById = new Map(kept.map(r => [r.id, r]));
  const modules: NavModule[] = [];

  for (const m of moduleDtos ?? []) {
    const root = rootById.get(m.featureId);
    const extra = adopted.get(m.featureId) ?? [];
    const rawChildren = [...(root?.children ?? []), ...extra];
    const children = filterByPerms(rawChildren).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));

    const label = displayTerm((m.aliasName?.trim() || m.featureName || '').trim());
    const path = normPath(m.path) ?? normPath(root?.path ?? null);
    const isLocked = m.isEnabled; // <- THE inversion point. isEnabled===true means LOCKED.

    // A live module with nothing to show and nowhere to go is unreachable — drop it.
    const defaultPath = firstAccessibleLeaf(children) ?? path;
    if (!isLocked && !children.length && !defaultPath) continue;

    modules.push({
      id: m.featureId,
      parentId: null,
      kind: 'module',
      label,
      icon: iconFor(m.iconName, 'module'),
      path,
      order: root?.order ?? 0,
      needsTitle: label.length > TITLE_THRESHOLD,
      perms: root?.perms ?? NO_PERMS,
      children,
      isLocked,
      defaultPath,
    });
  }

  const bySort = (a: NavModule, b: NavModule) => a.order - b.order || a.label.localeCompare(b.label);
  const live = modules.filter(m => !m.isLocked).sort(bySort);
  const locked = modules.filter(m => m.isLocked).sort(bySort);

  const byId = new Map<number, NavNode>();
  const collect = (n: NavNode): void => {
    byId.set(n.id, n);
    n.children.forEach(collect);
  };
  [...live, ...locked].forEach(collect);

  const managed = new Set<string>();
  for (const raw of menuPaths) {
    const n = normPath(raw);
    if (n) managed.add(n.toLowerCase());
  }

  // Only live modules take part in active-state matching; a locked module is not navigable.
  return { live, locked, byId, index: buildPathIndex(live), menuManagedPaths: managed };
}
